import { apiBaseUrl } from 'constants/apiBaseUrl'
import { getAuthToken } from 'core/api/axiosClient'

export type AssistantToolSchema = {
  type: 'object'
  properties: Record<string, { type: string; description: string }>
  required: string[]
}

export type AssistantToolRecord = {
  name: string
  description: string
  schema: AssistantToolSchema
  enabled: boolean
  edited: boolean
  lastEditedBy: { uid: string; name: string; email: string | null } | null
  lastEditedAt: string | null
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

export async function fetchAssistantTools(): Promise<AssistantToolRecord[]> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/tools`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(`Could not load tools (${response.status})`)
  const body = (await response.json()) as { tools?: AssistantToolRecord[] }
  return Array.isArray(body.tools) ? body.tools : []
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown }
    if (typeof body.error === 'string' && body.error.trim()) return body.error
  } catch {
    // The status is enough when the body is not JSON.
  }
  return fallback
}

export async function createAssistantTool(input: {
  intent: string
  enabled: boolean
}): Promise<AssistantToolRecord> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/tools`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  })
  if (!response.ok) throw new Error(await errorMessage(response, 'Could not save this tool'))
  const body = (await response.json()) as { tool: AssistantToolRecord }
  return body.tool
}

export async function saveAssistantTool(
  name: string,
  update: { description: string; enabled: boolean; schema?: AssistantToolSchema },
): Promise<AssistantToolRecord> {
  const response = await fetch(
    `${apiBaseUrl}/portal/admin/assistant/tools/${encodeURIComponent(name)}`,
    {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify(update),
    },
  )
  if (!response.ok) throw new Error(await errorMessage(response, 'Could not save this tool'))
  const body = (await response.json()) as { tool: AssistantToolRecord }
  return body.tool
}
